import React, { useEffect, useState } from 'react'
import { usePrevious } from 'react-use'
import { router, usePage } from '@inertiajs/react'

import { Card, Select, Option, Typography } from '@material-tailwind/react'
import Chart from 'react-apexcharts'
import pickBy from 'lodash/pickBy'
import { format } from 'date-fns'

import Layout from '@/Shared/Layout'
import DatePicker from '@/Shared/DatePicker'
import DashboardFilter from '@/Shared/DashboardFilter'

const Dashboard = () => {
    const {
        available, consumed, remaining, trends, months, filters, usersLastLogin, auth: {
            user: { role }
        }
    } = usePage().props

    const [values, setValues] = useState({
        service: filters.service || '',
        startDate: filters.startDate || '',
        endDate: filters.endDate || ''
    })

    const [items] = useState([
        'Brooms (1.5 Kg)',
        'Wiper',
        'Normal Waste Bags 21x28 (KG)',
        'Normal Waste Bags 30x30 (KG)',
        'Normal Waste Bags 39x45 (KG)',
        'Furniture Dusters',
        'Disinfectant (2.75 Lit Bottle)',
        'Hand Wash Liquid (Liters)',
        'Air Freshener (Bottles)',
        'Mop Pads',
        'Liquid Toilet Cleaner (900 ml Bottle)',
        'Floor Mops Standard',
        'Hand Wash Dispenser',
        'Floor cleaning Towels',
        'Air Freshener Dispenser (Machines)',
        'Air Freshener Dispenser Refills',
    ])

    const prevValues = usePrevious(values)

    useEffect(() => {
        if (prevValues) {
            const query = Object.keys(pickBy(values)).length
                ? pickBy(values)
                : { remember: 'forget' }
            router.get(route(route().current()), query, {
                replace: true,
                preserveState: true
            })
        }
    }, [values])

    const options = {
        dataLabels: {
            enabled: true
        },
        plotOptions: {
            bar: {
                borderRadius: 10
            }
        },
        stroke: {
            show: true,
            width: 2,
            colors: ["transparent"],
        },
        chart: {
            id: 'basic-bar',
            width: '900'
        },
        xaxis: {
            categories: items
        }
    }

    const series = [
        {
            name: 'Total',
            data: available
        },
        {
            name: 'Consumed',
            data: consumed
        }
    ]

    const series2 = [{
        name: 'Remaining',
        data: remaining
    }]

    const options2 = {
        colors: ['#2563eb', '#16a34a', '#dc2626', '#52525b'],
        chart: {
            height: 350,
            type: 'bar'
        },
        plotOptions: {
            bar: {
                distributed: true,
                dataLabels: {
                    position: 'top'
                }
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function(val) {
                return val + '%'
            },
            offsetY: -20,
            style: {
                fontSize: '12px',
                colors: ['#304758']
            }
        },

        xaxis: {
            categories: items,
            position: 'top',
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            },
            crosshairs: {
                fill: {
                    type: 'gradient',
                    gradient: {
                        colorFrom: '#D8E3F0',
                        colorTo: '#BED1E6',
                        stops: [0, 100],
                        opacityFrom: 0.4,
                        opacityTo: 0.5
                    }
                }
            },
            tooltip: {
                enabled: true
            }
        },
        yaxis: {
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            },
            labels: {
                show: false,
                formatter: function(val) {
                    return val + '%'
                }
            }

        },
        title: {
            text: 'Remaining Items Quantity',
            floating: true,
            offsetY: 330,
            align: 'center',
            style: {
                color: '#444'
            }
        }
    }

    const series3 = [{
        name: 'Items',
        data: trends
    }]

    const options3 = {
        colors: ['#fcd34d'],
        chart: {
            height: 350,
            type: 'line',
            zoom: {
                enabled: false
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'straight'
        },
        title: {
            text: 'Item Trends by Month',
            align: 'left'
        },
        grid: {
            row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
            }
        },
        xaxis: {
            categories: months
        }
    }

    const options4 = {
        dataLabels: {
            enabled: false
        },
        chart: {
            id: 'basic-bar',
            width: '900'
        },
        title: {
            text: 'User Last Login At',
            align: 'left'
        },
        xaxis: {
            categories: Object.keys(usersLastLogin)
        },
        yaxis: {
            labels: {
                formatter: function(value) {
                    return format(value, 'dd/MM/yyyy');
                }
            }
        }
    }

    const series4 = [
        {
            name: 'Date',
            data: Object.values(usersLastLogin).map(dateString => new Date(dateString).getTime())
        }
    ]

    return (
        <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>

            <Card className="mt-4 p-4">
                <div className="my-2 flex justify-between">
                    <Typography variant="h4" color="blue-gray" className="mb-2">
                        Consumable Items
                    </Typography>

                    <DashboardFilter values={values} setValues={setValues} />

                    <div className="hidden gap-x-2 xl:flex">
                        <DatePicker
                            label="Select Start Date"
                            date={values.startDate}
                            maxDate={values.endDate}
                            setDate={date =>
                                setValues(prev => ({
                                    ...prev,
                                    startDate: date
                                }))
                            }
                        />

                        <DatePicker
                            label="Select End Date"
                            date={values.endDate}
                            minDate={values.startDate}
                            setDate={date =>
                                setValues(prev => ({
                                    ...prev,
                                    endDate: date
                                }))
                            }
                        />

                        <div className="w-72">
                            <Select
                                label="Select Service"
                                onChange={value =>
                                    setValues(prev => ({
                                        ...prev,
                                        service: value
                                    }))
                                }
                                value={values.service}
                            >
                                <Option value="janitorial">Janitorial</Option>
                                <Option value="security">Security</Option>
                                <Option value="ct-scan">CT Scan</Option>
                                <Option value="mepg">MEPG</Option>
                            </Select>
                        </div>
                    </div>
                </div>

                <Chart
                    options={options}
                    series={series}
                    type="bar"
                    height="600"
                    className="mt-4"
                />
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-2 mt-4 gap-4">
                <Card className="p-4">
                    <Chart
                        options={options2}
                        series={series2}
                        type="bar"
                        height="600"
                        className="mt-4"
                    />
                </Card>

                {role === '1' && (
                    <Card className="p-4">
                        <Chart
                            options={options3}
                            series={series3}
                            type="line"
                            height="600"
                            className="mt-4"
                        />
                    </Card>
                )}
            </div>

            {role === '1' && (
                <Card className="p-4 mt-4">
                    <Chart
                        options={options4}
                        series={series4}
                        type="bar"
                        height="600"
                        className="mt-4"
                    />
                </Card>
            )}
        </div>
    )
}

Dashboard.layout = page => <Layout title="Dashboard" children={page} />

export default Dashboard
